import React from 'react';
import {
  Box,
  Text,
  TextInput,
  ActionIcon,
  Tooltip,
  Menu,
  Divider,
  Paper,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faMagnifyingGlass,
  faCopy,
  faChevronDown,
  faChevronUp,
  faEllipsisVertical,
  faDownload,
  faIndent,
  faSitemap,
  faCode,
} from '@fortawesome/pro-light-svg-icons';
import JsonView from '@microlink/react-json-view';
import classes from './JsonViewerContent.module.css';

interface JsonViewerContentProps {
  readonly data: unknown;
}

type ViewMode = 'tree' | 'raw';

const DARK_JSON_THEME = {
  base00: '#1a1b1e',
  base01: '#25262b',
  base02: '#373a40',
  base03: '#909296',
  base04: '#909296',
  base05: '#c1c2c5',
  base06: '#c1c2c5',
  base07: '#c1c2c5',
  base08: '#fa5252',
  base09: '#fd7e14',
  base0A: '#fd7e14',
  base0B: '#51cf66',
  base0C: '#228be6',
  base0D: '#228be6',
  base0E: '#be4bdb',
  base0F: '#fa5252',
};

/** react-json-view requires an object or array at the root. Cmail's
 *  "View Extracted Data" path already opens this viewer with `null` when
 *  extractedData is missing or not JSON; coerce that to `{}` so the tree
 *  does not throw. Field count / raw / copy still use the original `data`. */
const getJsonViewSource = (data: unknown): object => {
  if (data !== null && typeof data === 'object') {
    return data;
  }
  return {};
};

const getFieldCount = (obj: unknown): number => {
  if (!obj || typeof obj !== 'object') {
    return 0;
  }

  let count = Object.keys(obj).length;

  Object.values(obj).forEach((value) => {
    if (value && typeof value === 'object') {
      count += getFieldCount(value);
    }
  });

  return count;
};

const getMaxDepth = (obj: unknown, currentDepth = 1): number => {
  if (!obj || typeof obj !== 'object') {
    return currentDepth;
  }

  if (Array.isArray(obj)) {
    return Math.max(...obj.map((item) => getMaxDepth(item, currentDepth + 1)));
  }

  return Math.max(
    ...Object.values(obj).map((value) => getMaxDepth(value, currentDepth + 1))
  );
};

const getNodeDepth = (field: { parent?: unknown }): number => {
  let depth = 1;
  let current = field as { parent?: { parent?: unknown } };
  while (current.parent) {
    depth++;
    current = current.parent;
  }
  return depth;
};

const getJsonTheme = () => {
  const isDark =
    document.documentElement.getAttribute('data-mantine-color-scheme') ===
    'dark';
  return isDark ? DARK_JSON_THEME : 'rjv-default';
};

function getHighlightedRawParts(
  rawText: string,
  searchTerm: string,
  searchRegex: RegExp
) {
  return rawText.split(searchRegex).map((part, partIndex) => {
    if (part.toLowerCase() === searchTerm.toLowerCase()) {
      return (
        <span key={partIndex} className={classes.highlight}>
          {part}
        </span>
      );
    }
    return part;
  });
}

interface JsonTreeViewProps {
  readonly data: unknown;
  readonly isExpanded: boolean;
  readonly collapsedLevels: readonly number[];
}

function JsonTreeView({
  data,
  isExpanded,
  collapsedLevels,
}: JsonTreeViewProps) {
  return (
    <JsonView
      src={getJsonViewSource(data)}
      theme={getJsonTheme()}
      name={false}
      displayDataTypes={false}
      enableClipboard
      style={{
        fontSize: '0.875rem',
        fontFamily: 'monospace',
      }}
      collapseStringsAfterLength={60}
      shouldCollapse={(field) => {
        if (isExpanded) {
          return false;
        }
        if (collapsedLevels.length === 0) {
          return true;
        }
        // rjv passes a runtime `parent` chain not present on the
        // published CollapsedFieldProps type — cast to the shape we walk.
        const depth = getNodeDepth(field as unknown as { parent?: unknown });
        return collapsedLevels.includes(depth);
      }}
    />
  );
}

interface RawJsonViewProps {
  readonly data: unknown;
  readonly indentSize: number;
  readonly searchTerm: string;
  readonly searchRegex: RegExp | null;
}

function RawJsonView({
  data,
  indentSize,
  searchTerm,
  searchRegex,
}: RawJsonViewProps) {
  const rawText = JSON.stringify(data, null, indentSize);
  if (!searchRegex) {
    return <Paper className={classes.rawJson}>{rawText}</Paper>;
  }
  return (
    <Paper className={classes.rawJson}>
      {getHighlightedRawParts(rawText, searchTerm, searchRegex)}
    </Paper>
  );
}

/**
 * Toolbar + search + tree/raw JSON viewer, without any outer chrome (modal
 * frame or slide-out shell). Shared by `JsonViewerModal` and the Ask Moo
 * `JsonViewerPanel` so the two presentations can't drift apart. The caller
 * owns the surrounding title/close chrome.
 */
export const JsonViewerContent = ({ data }: JsonViewerContentProps) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isExpanded, setIsExpanded] = React.useState(true);
  const [viewMode, setViewMode] = React.useState<ViewMode>('tree');
  const [menuOpened, setMenuOpened] = React.useState(false);
  const [indentSize, setIndentSize] = React.useState(2);
  const [collapsedLevels, setCollapsedLevels] = React.useState<number[]>([]);

  const totalFields = React.useMemo(() => getFieldCount(data), [data]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleExpandAll = () => {
    setIsExpanded(true);
    setCollapsedLevels([]);
  };

  const handleCollapseAll = () => {
    setIsExpanded(false);
    const maxDepth = getMaxDepth(data);
    setCollapsedLevels(
      Array.from({ length: maxDepth - 1 }, (_, idx) => idx + 2)
    );
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, indentSize));
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(data, null, indentSize)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'extracted-data.json';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleIndentSizeChange = (size: number) => {
    setIndentSize(size);
    setMenuOpened(false);
  };

  const searchRegex = searchTerm
    ? new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    : null;

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Text size="xs" className={classes.fieldCount}>
          {totalFields} {totalFields === 1 ? 'field' : 'fields'}
        </Text>
        <Box className={classes.toolbarActions}>
          <Tooltip label="Copy All">
            <ActionIcon variant="subtle" size="sm" onClick={handleCopyAll}>
              <FontAwesomeIcon icon={faCopy} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Download JSON">
            <ActionIcon variant="subtle" size="sm" onClick={handleDownload}>
              <FontAwesomeIcon icon={faDownload} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={isExpanded ? 'Collapse All' : 'Expand All'}>
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={isExpanded ? handleCollapseAll : handleExpandAll}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
              />
            </ActionIcon>
          </Tooltip>
          <Divider orientation="vertical" className={classes.toolbarDivider} />
          <Box className={classes.viewModeToggle}>
            <Tooltip label="Tree View">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setViewMode('tree')}
                className={
                  viewMode === 'tree'
                    ? classes.activeViewMode
                    : classes.inactiveViewMode
                }
              >
                <FontAwesomeIcon icon={faSitemap} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Raw JSON">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setViewMode('raw')}
                className={
                  viewMode === 'raw'
                    ? classes.activeViewMode
                    : classes.inactiveViewMode
                }
              >
                <FontAwesomeIcon icon={faCode} />
              </ActionIcon>
            </Tooltip>
          </Box>
          <Menu opened={menuOpened} onChange={setMenuOpened} zIndex={10000}>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" aria-label="More Options">
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown miw="12.5rem">
              <Menu.Item
                leftSection={<FontAwesomeIcon icon={faIndent} />}
                onClick={() => handleIndentSizeChange(2)}
              >
                Indent Size: 2
              </Menu.Item>
              <Menu.Item
                leftSection={<FontAwesomeIcon icon={faIndent} />}
                onClick={() => handleIndentSizeChange(4)}
              >
                Indent Size: 4
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      </Box>

      <Box className={classes.searchBar}>
        <TextInput
          placeholder="Search in JSON..."
          value={searchTerm}
          onChange={handleSearch}
          variant="unstyled"
          leftSection={<FontAwesomeIcon icon={faMagnifyingGlass} />}
          rightSection={
            searchTerm ? (
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setSearchTerm('')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </ActionIcon>
            ) : null
          }
        />
      </Box>

      <Box className={classes.jsonViewer}>
        {viewMode === 'tree' && (
          <JsonTreeView
            data={data}
            isExpanded={isExpanded}
            collapsedLevels={collapsedLevels}
          />
        )}
        {viewMode === 'raw' && (
          <RawJsonView
            data={data}
            indentSize={indentSize}
            searchTerm={searchTerm}
            searchRegex={searchRegex}
          />
        )}
      </Box>
    </Box>
  );
};
